
import React, {useState} from "react";
import { Table } from "antd";
import { useSelector } from "react-redux";
import moment from "moment";
import commaNumber from "comma-number";

const format = commaNumber.bindWith(",", ".");

const ExpenseRecordTable = () => {
  const {
    list,
    details,
    filterResult,
    filterResultDetails,
    loading,
    filters,
  } = useSelector((state) => state.expenseRecord);

  const { selectedFilterType, selectedRecordType } = filters;
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };
  
  // ✅ Build rows for "with details" mode
  const buildWithDetailsData = (data) => {
    let rows = [];
    let counter = 1; // voucher counter

    data.forEach((voucher) => {
      let subTotal = 0;

      voucher.details.forEach((d, dIdx) => {
        subTotal += parseFloat(d.exp_amount || 0);

        rows.push({
          key: `${voucher.id}-${dIdx}`,
          exp_code: voucher.exp_code,
          created_date: voucher.created_date,
          acc_name: voucher.acc_name,
          full_name: voucher.full_name,
          narration: voucher.narration,
          paymentFor: d.acc_name,
          exp_amount: d.exp_amount,
          isSubtotal: false,
          isFirstDetail: dIdx === 0, // ✅ mark first detail row
          voucherNo: counter, // ✅ assign voucher counter
        });
      });

      // Push subtotal row
      rows.push({
        key: `subtotal-${voucher.id}`,
        exp_code: voucher.exp_code,
        isSubtotal: true,
        subTotal,
      });

      counter++; // ✅ increment after each voucher
    });

    return rows;
  };

  // Without details columns
  const withoutDetailsColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "VCH No", dataIndex: "exp_code", width: 100 },
    {
      title: "Date",
      dataIndex: "created_date",
      render: (date) => moment(date).format("YYYY-MM-DD"),
      width: 120,
    },
    { title: "Payment From", dataIndex: "acc_name", width: 200 },
    { title: "Pay To", dataIndex: "pay_to", width: 200 },
    { title: "Payment By", dataIndex: "full_name", width: 150 },
    { title: "Description", dataIndex: "narration", width: 200 },
    {
      title: "Total Amount",
      dataIndex: "exp_total",
      render: (val) => format(parseFloat(val || 0).toFixed(2)),
      align: "right",
      width: 120,
    },
  ];

  // Expense details columns
  const expenseDetailsColumns = [
    { title: "No.", render: (_, __, index) => index + 1, width: 60 },
    { title: "VCH No", dataIndex: "exp_code", width: 100 },
    {
      title: "Date",
      dataIndex: "created_date",
      render: (date) => moment(date).format("YYYY-MM-DD"),
      width: 120,
    },
    { title: "Payment For", dataIndex: "acc_name", width: 200 },
    { title: "Payment By", dataIndex: "full_name", width: 150 },
    { title: "Description", dataIndex: "narration", width: 200 },
    {
      title: "Total Amount",
      dataIndex: "exp_amount",
      render: (val) => format(parseFloat(val || 0).toFixed(2)),
      align: "right",
      width: 120,
    },
  ];

  // With details columns
  const withDetailsColumns = [
    {
      title: "No.",
      render: (_, record) =>
        record.isSubtotal ? "" : record.isFirstDetail ? record.voucherNo : "",
      width: 60,
    },
    {
      title: "VCH",
      dataIndex: "exp_code",
      render: (val, record) => (record.isSubtotal ? "" : val),
      width: 100,
    },
    {
      title: "Date",
      dataIndex: "created_date",
      render: (date, record) =>
        record.isSubtotal ? "" : moment(date).format("YYYY-MM-DD"),
      width: 120,
    },
    {
      title: "Payment From",
      dataIndex: "acc_name",
      render: (val, record) => (record.isSubtotal ? "" : val),
      width: 200,
    },
    {
      title: "Payment By",
      dataIndex: "full_name",
      render: (val, record) => (record.isSubtotal ? "" : val),
      width: 150,
    },
    {
      title: "Payment For",
      dataIndex: "paymentFor",
      render: (val, record) => (record.isSubtotal ? "" : val),
      width: 200,
    },
    {
      title: "Description",
      dataIndex: "narration",
      render: (val, record) =>
        record.isSubtotal ? (
          <strong style={{ float: "right" }}>Sub Total</strong>
        ) : (
          val
        ),
      width: 200,
    },
    {
      title: "Payment Amount",
      dataIndex: "exp_amount",
      render: (val, record) => {
        if (record.isSubtotal) {
          return <strong>{format(record.subTotal.toFixed(2))}</strong>;
        }
        return format(parseFloat(val || 0).toFixed(2));
      },
      align: "right",
      width: 120,
    },
  ];

  // Main render
  const renderTable = () => {
    if (selectedFilterType?.filterType === "By Expense") {
      const totals = details.reduce(
        (sum, item) => sum + parseFloat(item.exp_amount || 0),
        0
      );

      return (
        <Table
          rowKey="id"
          columns={expenseDetailsColumns}
          dataSource={details}
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
          }}
          onChange={handleTableChange}
          bordered
          scroll={{ x: "max-content" }}
          size="middle"
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={6}>
                <strong>Grand Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="right">
                <strong>{format(totals.toFixed(2))}</strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      );
    } else if (selectedRecordType?.recordType === "with details") {
      const rows = buildWithDetailsData(filterResultDetails);
      const grandTotal = filterResultDetails.reduce(
        (sum, item) => sum + parseFloat(item.exp_total || 0),
        0
      );

      return (
        <Table
          rowKey="key"
          columns={withDetailsColumns}
          dataSource={rows}
          loading={loading}
          pagination={false}
          bordered
          scroll={{ x: "max-content" }}
          size="middle"
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={7}>
                <strong>Grand Total :</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} align="right">
                <strong>{format(grandTotal.toFixed(2))}</strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      );
    } else {
      const totals = filterResult.reduce(
        (sum, item) => sum + parseFloat(item.exp_total || 0),
        0
      );

      return (
        <Table
          rowKey="id"
          columns={withoutDetailsColumns}
          dataSource={filterResult}
          loading={loading}
          pagination={{ pageSize: 10 }}
          bordered
          scroll={{ x: "max-content" }}
          size="middle"
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={7}>
                <strong>Grand Total</strong>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} align="right">
                <strong>{format(totals.toFixed(2))}</strong>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      );
    }
  };

  return <div>{renderTable()}</div>;
};

export default ExpenseRecordTable;
